import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import { trackEvent } from '@offerzen/pinata-shame'
import { hasTemplate } from '@/core/helpers/positions'
import { parseDate } from '@/core/helpers/dateTimeHelper'
import { selectSalaryConversion } from '@/core/helpers/candidateHelpers'

const calculateRelativeSalary = (interviewRequestSalary, candidatePreferredSalary) => {
  if (interviewRequestSalary > candidatePreferredSalary) return 'above'

  if (interviewRequestSalary < candidatePreferredSalary) return 'below'

  return 'equals'
}

export const sentInterviewRequestAnalyticsBuilder = ({
  candidate,
  competingIRs,
  positions,
  interviewRequest,
  messageSameAsTemplate,
  hiringTeam,
  candidateHasConvertedSalary,
  workplacePolicy,
  isRecommendedCandidate,
}) => {
  let withJobSpec
  let positionHasTemplate
  let withUnmodifiedTemplate

  const withPosition = !!interviewRequest.job_opening

  if (withPosition) {
    const attachedPosition = positions.find(position => position.id === interviewRequest.job_opening.id)
    withJobSpec = !!(attachedPosition.attachment || attachedPosition.description)

    positionHasTemplate = hasTemplate(attachedPosition.message_template)
    if (positionHasTemplate) withUnmodifiedTemplate = messageSameAsTemplate
  }
  const hiringTeamCurrency = hiringTeam.currencyCode.toUpperCase()

  const convertedSalary = selectSalaryConversion({
    salaryConversions: candidate.salaryConversions,
    hiringTeam,
  })
  const relativeSalary = calculateRelativeSalary(interviewRequest.salary, convertedSalary?.value)

  let workplacePolicyLocation = []
  if (workplacePolicy?.anywhere) workplacePolicyLocation = ['Anywhere']
  if (workplacePolicy?.comprehensive_locations)
    workplacePolicyLocation = [...workplacePolicyLocation, ...workplacePolicy.comprehensive_locations]

  const properties = {
    action: 'sent interview request',
    source: 'candidate detail',
    candidateLiveDays: differenceInCalendarDays(new Date(), parseDate(candidate.transitioned_at)),
    competingIrCount: competingIRs.length,
    irId: interviewRequest.id,
    candidateId: candidate.id,
    positionId: interviewRequest.job_opening?.id,
    relativeSalary,
    withPosition,
    positionHasTemplate,
    withJobSpec,
    withUnmodifiedTemplate,
    hiringTeamId: hiringTeam.id,
    hiringTeamCurrency,
    hiringTeamRemunerationPeriod: hiringTeam.remunerationPeriod.toUpperCase(),
    candidateHasConvertedSalary,
    workplacePolicyType: workplacePolicy?.type,
    workplacePolicyLocation,
    company_name: interviewRequest.company_profile.name,
    companyLinkedToAts: interviewRequest.company_profile.linked_to_ats,
    linkedAtsJob: !!interviewRequest.job_opening?.linked_ats_job,
    recommended_candidate: !!isRecommendedCandidate,
    company_id: interviewRequest.company_profile.id,
  }

  return properties
}

export const trackBasicInfoOpenTooltip = ({ candidateId, shownCurrency, remunerationPeriod, originalCurrency }) => {
  trackEvent('Company: candidate details action', {
    source: 'candidate detail',
    action: 'toggled tooltip',
    topic: 'converted salary',
    shownCurrency,
    remunerationPeriod,
    originalCurrency,
    candidateId,
  })
}

export const trackCitizenshipAttachmentDownload = (candidateId, citizenshipStatus) => {
  trackEvent('Company: candidate details action', {
    action: 'downloaded attachment',
    source: 'candidate detail',
    origin: 'citizenship status',
    candidateId,
    citizenshipStatus,
  })
}

export const trackCitizenshipExternalGuideClick = citizenshipStatus => {
  trackEvent('Company: candidate details action', {
    action: 'clicked external link',
    source: 'candidate detail',
    origin: 'citizenship status',
    target: 'public guide',
    citizenshipStatus,
  })
}

export const trackFavouriteAnalytics = ({
  source,
  isCompanyAccountManager,
  candidateId,
  profileId,
  hiringTeamId,
  positionId,
  companyId,
  companyName,
  isRecommendedCandidate,
}) => {
  trackEvent('Company: Favourite', {
    source,
    is_company_account_manager: isCompanyAccountManager,
    candidate_id: candidateId,
    profile_id: profileId,
    hiring_team_id: hiringTeamId,
    position_id: positionId,
    recommended_candidate: !!isRecommendedCandidate,
    company_id: companyId,
    company_name: companyName,
  })
}

export const trackCandidateDetailView = ({ candidateId, hiringTeam, candidateHasConvertedSalary }) => {
  trackEvent('Company: candidate details action', {
    action: 'viewed candidate profile',
    source: 'candidate detail',
    candidateId,
    hiringTeamId: hiringTeam && hiringTeam.id,
    hiringTeamCurrency: hiringTeam && hiringTeam.currencyCode,
    hiringTeamRemunerationPeriod: hiringTeam && hiringTeam.remunerationPeriod,
    candidateHasConvertedSalary,
  })
}

/**
 *
 * @param {Object} params
 * @param {Object} params.candidate
 * @param {Object[]} params.competingIRs
 * @param {Object[]} params.positions
 * @param {Object} params.interviewRequest
 * @param {boolean} params.messageSameAsTemplate
 * @param {Object} params.hiringTeam
 * @param {boolean} params.candidateHasConvertedSalary
 * @param {Object} params.workplacePolicy
 * @param {boolean} params.isRecommendedCandidate
 *
 * @returns {import('redux').Action<any>}
 */

export const trackInterviewRequestSubmissionSuccess = ({
  candidate,
  competingIRs,
  positions,
  interviewRequest,
  messageSameAsTemplate,
  hiringTeam,
  candidateHasConvertedSalary,
  workplacePolicy,
  isRecommendedCandidate,
}) => {
  const analyticsProps = sentInterviewRequestAnalyticsBuilder({
    candidate,
    competingIRs,
    positions,
    interviewRequest,
    messageSameAsTemplate,
    hiringTeam,
    candidateHasConvertedSalary,
    workplacePolicy,
    isRecommendedCandidate,
  })

  trackEvent('Company: interview request action', analyticsProps)
}

export const trackInterviewRequestSubmissionFailure = ({
  candidateId,
  positionId,
  hasSalary,
  errors,
  candidateHasConvertedSalary,
  hiringTeam,
  remotePolicy,
  isRecommendedCandidate,
} = {}) => {
  let workplacePolicyLocation = []
  if (remotePolicy?.isAnywhere) workplacePolicyLocation = ['Anywhere']
  if (remotePolicy?.locations) workplacePolicyLocation = [...workplacePolicyLocation, ...remotePolicy.locations]
  trackEvent('Company: interview request action', {
    action: 'failed send interview request',
    source: 'candidate detail',
    candidateId,
    positionId,
    hasSalary,
    errors,
    candidateHasConvertedSalary,
    hiringTeamId: hiringTeam?.id,
    hiringTeamCurrency: hiringTeam?.currencyCode,
    hiringTeamRemunerationPeriod: hiringTeam?.remunerationPeriod,
    workplacePolicyType: remotePolicy?.type,
    workplacePolicyLocation,
    recommended_candidate: !!isRecommendedCandidate,
  })
}
